import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Box,
  Collapse,
  Group,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { Bookmark, ChevronDown, ChevronUp } from 'react-feather';
import { ContentCard } from '../../../components/common/ContentCard';
import type { PriorCommitmentBlock } from '../../../services/api/preSessionReflection';
import { usePriorCommitmentReflectionAutosave } from './usePriorCommitmentReflectionAutosave';

const TITLE_COLOR = '#1A1A2E';
const QUOTE_BG = '#F9FAFB';
const QUOTE_BORDER = '#E5E7EB';

interface Props {
  sessionId: string;
  block: PriorCommitmentBlock;
  isLocked: boolean;
}

/**
 * "Reflect on Prior Commitments" card (MED-937 / E3.1b; polished by MED-1114).
 *
 * Renders the participant's commitment from the immediately preceding session
 * as a read-only quote, plus a free-text "How do you think things went?"
 * textarea that autosaves (no Save button) on the same debounced cadence as the
 * main reflection form.
 *
 * The card only renders when its parent has been handed a non-null
 * `prior_commitment` block from the payload — the visibility gating lives in
 * the API and the page. This component never decides whether to show itself.
 *
 * MED-1114 — built on the shared `ContentCard` (elevated, radius 16). Collapse
 * is load-time only (Option A): the card seeds collapsed when the response is
 * already answered (a return visit, no flash) and expanded otherwise. Because
 * there's no Save button, it NEVER re-collapses while the participant edits —
 * the seed is the only automatic collapse; the chevron toggles manually.
 */
export function PriorCommitmentCard({ sessionId, block, isLocked }: Props) {
  const [value, setValue] = useState(block.reflection_text);
  const { state, scheduleSave } =
    usePriorCommitmentReflectionAutosave(sessionId);
  // Seed collapsed when already answered so a return visit loads collapsed
  // without flashing open. Load-time only — never recomputed on edit.
  const [collapsed, setCollapsed] = useState(
    () => block.reflection_text.trim().length > 0
  );

  // Re-sync local state when a fresh payload arrives (page refetch).
  useEffect(() => {
    setValue(block.reflection_text);
  }, [block.reflection_text]);

  const handleChange = (text: string) => {
    setValue(text);
    scheduleSave(text);
  };

  const saveHint =
    state === 'saving'
      ? 'Saving…'
      : state === 'saved'
        ? 'All changes saved.'
        : state === 'error'
          ? 'Save failed — try again.'
          : undefined;

  return (
    <ContentCard
      variant="elevated"
      radius={16}
      padding="lg"
      contentGap={0}
      fill={false}
      header={
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="xs" align="center">
            <Bookmark size={18} color="#6B7280" aria-hidden />
            <Text fw={600} fz={17} c={TITLE_COLOR}>
              Reflect on Prior Commitments
            </Text>
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => setCollapsed(c => !c)}
            aria-label={
              collapsed
                ? 'Expand prior commitments'
                : 'Collapse prior commitments'
            }
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
          </ActionIcon>
        </Group>
      }
    >
      <Collapse in={!collapsed}>
        <Stack gap="md" mt="md">
          <Text size="sm" c="dimmed">
            From your last session, you committed to:
          </Text>
          <Box
            aria-label="Prior session commitment"
            style={{
              border: `1px solid ${QUOTE_BORDER}`,
              borderRadius: 8,
              padding: '12px 14px',
              background: QUOTE_BG,
            }}
          >
            <Text size="sm" fs="italic">
              “{block.commitment_text}”
            </Text>
          </Box>

          <Stack gap={4}>
            <Text
              component="label"
              htmlFor="prior-commitment-reflection"
              fw={600}
              size="sm"
            >
              How do you think things went?
            </Text>
            <Textarea
              id="prior-commitment-reflection"
              data-testid="prior-commitment-reflection"
              value={value}
              onChange={e => handleChange(e.currentTarget.value)}
              autosize
              minRows={3}
              disabled={isLocked}
            />
            {!isLocked && saveHint && (
              <Text size="xs" c="dimmed" aria-live="polite">
                {saveHint}
              </Text>
            )}
          </Stack>
        </Stack>
      </Collapse>
    </ContentCard>
  );
}

export default PriorCommitmentCard;
